
/** @locked-file Do not modify without explicit instruction */
import React, { useState } from 'react';
import { SectionWrapper } from './SectionWrapper';
import { Tab } from '../types';
import { Anchor } from 'lucide-react';

interface HeroProps {
  onNavigate: (tab: Tab) => void;
}

export const HeroComponent: React.FC<HeroProps> = ({ onNavigate }) => {
  const [isOpening, setIsOpening] = useState(false);
  const [loadingWidth, setLoadingWidth] = useState(0);

  const handleStartExploring = () => {
    if (isOpening) return;
    
    setLoadingWidth(100);
    
    setTimeout(() => {
      setIsOpening(true);
    }, 600);

    setTimeout(() => {
      onNavigate('about');
    }, 1400); 
  };

  return (
    <SectionWrapper className="min-h-[85vh] flex flex-col justify-center items-center text-center relative overflow-hidden pt-2 pb-8 md:pt-4 md:pb-12">
      
      {/* Background Atmosphere */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-dawn-gradient opacity-50 pointer-events-none animate-breath"></div>

      {/* The "Door" Background Characters */}
      <div className={`absolute inset-0 pointer-events-none flex justify-between items-center overflow-hidden transition-opacity duration-1000 ${isOpening ? 'opacity-0' : 'opacity-100'}`}>
        <div className={`w-1/2 h-full flex items-center justify-end pr-4 md:pr-10 transition-transform duration-1000 ease-in-out ${isOpening ? '-translate-x-[120%]' : 'translate-x-0'}`}>
          <span className="font-serif text-[18rem] md:text-[32rem] text-stone-900 opacity-[0.03] leading-none select-none">言</span>
        </div>
        <div className={`w-1/2 h-full flex items-center justify-start pl-4 md:pl-10 transition-transform duration-1000 ease-in-out ${isOpening ? 'translate-x-[120%]' : 'translate-x-0'}`}>
           <span className="font-serif text-[18rem] md:text-[32rem] text-stone-900 opacity-[0.03] leading-none select-none">屿</span>
        </div>
      </div>

      <div className={`z-10 max-w-4xl flex flex-col items-center transition-all duration-1000 ${isOpening ? 'translate-y-[-20px] opacity-0 blur-sm' : ''}`}>
        
        {/* Chandelier */}
        <div className="group relative flex flex-col items-center mb-2 md:mb-8 cursor-pointer">
           <div className="absolute -inset-x-10 -inset-y-4 z-20"></div>
           <div className="flex flex-col items-center origin-top animate-[swing_5s_ease-in-out_infinite] group-hover:animate-[swingHeavy_2s_ease-in-out_infinite]">
              <div className="h-8 md:h-16 w-[1px] bg-stone-300 transition-all duration-500"></div>
              <div className="relative">
                 <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-amber-200 shadow-[0_0_15px_3px_rgba(251,191,36,0.3)] relative z-10 transition-all duration-300 group-hover:shadow-[0_0_30px_8px_rgba(251,191,36,0.6)] group-hover:bg-amber-100"></div>
                 <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-amber-100/20 rounded-full blur-md group-hover:scale-125 transition-transform"></div>
              </div>
           </div>
        </div>

        {/* Text Content */}
        <div className="space-y-4 md:space-y-6 animate-fade-in px-4">
          <div className="flex flex-col items-center">
            <span className="text-[0.6rem] md:text-[0.7rem] font-mono tracking-[0.5em] text-sage-700 uppercase font-bold">Psychotherapy</span>
          </div>

          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-tight text-stone-900 tracking-tight font-black">
            在不安的世界里
            <br/>
            <span className="font-light italic text-stone-800 mt-1 md:mt-3 block relative inline-block">
               寻找内心的锚点
               <svg className="absolute -bottom-1 md:-bottom-2 left-0 w-full h-1 text-rose-300/80" viewBox="0 0 100 10" preserveAspectRatio="none">
                 <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="2" fill="none" />
               </svg>
            </span>
          </h1>

          <div className="font-serif text-stone-800 text-base md:text-xl lg:text-2xl leading-relaxed max-w-2xl mx-auto space-y-0.5 md:space-y-1.5 font-medium">
            <p>你无需去证明什么，</p>
            <p>你的存在本身就很有意义。</p>
          </div>

          <div className="pt-1 md:pt-3">
             <p className="text-[10px] md:text-sm text-stone-500 font-mono tracking-widest border-t border-stone-200 pt-3 md:pt-5 px-10 inline-block font-bold">
               陈瑞雪 · 精神分析动力学咨询师
             </p>
          </div>

          {/* Button Area */}
          <div className="pt-4 md:pt-6 flex flex-col items-center relative">
             <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-56 h-16 bg-amber-100/20 rounded-full blur-3xl animate-breath opacity-40"></div>
             </div>

             <div className="relative group">
                <div className="absolute -top-5 md:-top-7 left-1/2 -translate-x-1/2 text-rose-300 transition-all duration-700 group-hover:-translate-y-1 group-hover:text-rose-400">
                   <Anchor strokeWidth={1.5} className="w-4 h-4 md:w-5 md:h-5 drop-shadow-sm" />
                </div>

                <button 
                  onClick={handleStartExploring}
                  disabled={loadingWidth > 0}
                  className={`
                    relative px-10 py-3.5 md:px-14 md:py-4 bg-white/60 backdrop-blur-[2px] border border-stone-300 text-stone-800 
                    hover:border-stone-500 hover:text-stone-900 hover:shadow-[0_12px_35px_-10px_rgba(0,0,0,0.06)]
                    transition-all duration-700 rounded-sm text-sm tracking-[0.5em] overflow-hidden cursor-pointer
                    ${loadingWidth > 0 ? 'cursor-default' : ''}
                  `}
                >
                  <span className="relative z-10 font-black">开始探索</span>
                  <div 
                     className="absolute bottom-0 left-0 h-[2px] bg-rose-300 transition-all duration-[600ms] ease-out z-0 shadow-[0_0_8px_rgba(253,164,175,0.5)]"
                     style={{ width: `${loadingWidth}%` }}
                  ></div>
                  {loadingWidth === 0 && (
                    <div className="absolute inset-0 bg-stone-50/80 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-center duration-700 -z-10"></div>
                  )}
                </button>
             </div>
             
             <div className="mt-8 md:mt-6 text-[8px] md:text-[10px] text-stone-400 font-mono tracking-[0.5em] uppercase opacity-80 font-black">
                Inner Voyage Begins Here
             </div>
          </div>
        </div>

      </div>

      <style>{`
        @keyframes swing {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(2deg); }
          50% { transform: rotate(0deg); }
          75% { transform: rotate(-2deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes swingHeavy {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(10deg); }
          50% { transform: rotate(0deg); }
          75% { transform: rotate(-10deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>
    </SectionWrapper>
  );
};
